import numeral from "numeral";
import { CourseStatusOptions, ProgressStatus, StatusOptions } from "../../typings/types";
import { chain, escapeRegExp, find, isEqual, isNil, omitBy, snakeCase, sortBy } from "lodash";
// @ts-ignore
import { getColorName } from "ntc-ts";
import { format } from "date-fns";
import moment from "moment";
import { tokens } from "./tokens";

export const percentFormat = (value: string | number) => {
  if (value) {
    return `${numeral(+value * 100).format("0.[00]")}%`;
  } else {
    return "0%";
  }
};

export const getLetterFromCount = (count: number, isLowercase: boolean = false): string => {
  if (count < 1 || count > 26) throw new Error("Count must be between 1 and 26");

  const baseCode = isLowercase ? 96 : 64;
  return String.fromCharCode(baseCode + count);
};

export const getProgressColor = (label: ProgressStatus) =>
  label === "completed" ? tokens.brandGreen : label === "remaining" ? tokens.brandBlue : tokens.brandYellow;

export const status_color = (status: StatusOptions) => {
  switch (status) {
    case "open":
      return tokens.black;
    case "advisor-approval":
      return tokens.brandYellow;
    case "registrar-approval":
      return tokens.brandBlue;
    case "locked":
      return tokens.brandGreen;
    case "rejected":
      return tokens.error;
    default:
      return tokens.black;
  }
};

export const getCourseStatusColor = (status: CourseStatusOptions) => {
  switch (status) {
    case "active":
    case "approved": // backward-compat alias — DB may still have old value during migration
      return {
        bgcolor: `${tokens.brandGreen}26`,
        color: tokens.brandGreen,
      };
    case "inactive":
    case "rejected": // backward-compat alias
      return {
        bgcolor: `${tokens.error}26`,
        color: tokens.error,
      };
    case "archived":
      return {
        bgcolor: `${tokens.grayText}26`,
        color: tokens.grayText,
      };
    case "for-approval":
      return {
        bgcolor: `${tokens.info}26`,
        color: tokens.info,
      };
    case "draft":
      return {
        bgcolor: `${tokens.grayText}26`,
        color: tokens.grayText,
      };
    default:
      return {};
  }
};

export const getAssessmentStatusColor = (status: string) => {
  switch (snakeCase(status)) {
    case "graded":
      return {
        bgcolor: `${tokens.brandGreen}26`,
        color: tokens.brandGreen,
      };
    case "assigned":
      return {
        bgcolor: `${tokens.warningYellow}26`,
        color: tokens.warningYellow,
      };
    case "unknown":
      return {
        bgcolor: `${tokens.grayText}26`,
        color: tokens.grayText,
      };
    default:
      return {};
  }
};

export const getStudentCourseStatusColor = (status: string) => {
  switch (snakeCase(status).toLocaleLowerCase()) {
    case "assigned":
      return {
        bgcolor: `${tokens.info}26`,
        color: tokens.info,
      };
    case "approved":
      return {
        bgcolor: `${tokens.info}26`,
        color: tokens.info,
      };
    case "completed":
      return {
        bgcolor: `${tokens.brandGreen}26`,
        color: tokens.brandGreen,
      };
    case "scheduled":
      return {
        bgcolor: `${tokens.warningYellow}26`,
        color: tokens.warningYellow,
      };
    case "for_approval":
      return {
        bgcolor: `${tokens.warningYellow}26`,
        color: tokens.warningYellow,
      };
    case "late":
      return {
        bgcolor: `${tokens.error}26`,
        color: tokens.error,
      };
    case "draft":
      return {
        bgcolor: `${tokens.grayText}26`,
        color: tokens.grayText,
      };
    default:
      return {};
  }
};

export const sortedUniqueBy = (array: any[], field: string) => {
  return chain(array)
    .map(field)
    .sortBy((value) => (isNaN(value) ? value : Number(value)))
    .uniq()
    .value();
};

export const getUpdatedValues = (
  initialValue: Record<string, any>,
  newValue: Record<string, any>,
) => {
  return omitBy(newValue, (value, key) => isEqual(value, initialValue[key]));
};

export const serializeOptions = (array: Record<string, any>, key = "name") => {
  // add label or "id" to options
  return array.map((item: any) => {
    item.label = item[key] || item["id"];
    return item;
  });
};

export const getSchoolYearStatusColor = (status: string) => {
  switch (snakeCase(status).toLocaleLowerCase()) {
    case "draft":
      return {
        bgcolor: `${tokens.grayText}26`,
        color: tokens.grayText,
      };
    case "active":
      return {
        bgcolor: `${tokens.brandGreen}26`,
        color: tokens.brandGreen,
      };
    case "archived":
      return {
        bgcolor: `${tokens.error}26`,
        color: tokens.error,
      };
    default:
      return {};
  }
};

export const getClassStatusColor = (status: string) => {
  switch (snakeCase(status).toLocaleLowerCase()) {
    case "draft":
      return {
        bgcolor: `${tokens.grayText}26`,
        color: tokens.grayText,
      };
    case "active":
      return {
        bgcolor: `${tokens.brandGreen}26`,
        color: tokens.brandGreen,
      };
    case "archived":
      return {
        bgcolor: `${tokens.error}26`,
        color: tokens.error,
      };
    default:
      return {};
  }
};

export const stringToColor = (string: string) => {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
};

export const stringAvatar = (name: string, color?: string) => {
  const letters = name?.split(" ");
  const initials =
    letters.length == 1 ? `${letters[0][0]}${letters[0][1]}` : `${letters[0][0]}${name[1][0]}`;
  return {
    sx: {
      bgcolor: color ?? stringToColor(name),
    },
    children: initials,
  };
};

export const formatFileSize = (bytes: number, si = false, dp = 1) => {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const units = si
    ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + " " + units[u];
};

export const sortedBy = (arr: any[], sort_by: string) =>
  sortBy(arr, [
    (item) => (item[sort_by] || "").replace(/\d+/g, "").toLowerCase().trim(),
    (item) => parseInt((item[sort_by] || "").match(/\d+/)?.[0] || "0", 10),
  ]);

export const getStudentAssessmentColor = (status: string) => {
  switch (status.toLocaleLowerCase()) {
    case "open":
      return {
        bgcolor: `${tokens.brandGreen}26`,
        color: tokens.brandGreen,
      };
    case "locked":
      return {
        bgcolor: `${tokens.grayText}26`,
        color: tokens.grayText,
      };
    case "registrar-approval":
      return {
        bgcolor: `${tokens.warningYellow}26`,
        color: tokens.warningYellow,
      };
    case "adviser-approval":
      return {
        bgcolor: `${tokens.warningYellow}26`,
        color: tokens.warningYellow,
      };
    case "rejected":
      return {
        bgcolor: `${tokens.error}26`,
        color: tokens.error,
      };
    default:
      return {};
  }
};
export const getTeacherAssignmentStatus = (status: string) => {
  switch (snakeCase(status).toLocaleLowerCase()) {
    case "active":
      return {
        bgcolor: `${tokens.brandGreen}26`,
        color: tokens.brandGreen,
      };
    case "archived":
      return {
        bgcolor: `${tokens.error}26`,
        color: tokens.error,
      };
    default:
      return {};
  }
};

export const colorToName = (color: string) => {
  const match = getColorName(color);
  return match?.name ?? "Unknown";
};

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const urlToFile = async (url: string, filename: string, mimeType: string): Promise<File> => {
  const response = await fetch(url);
  const buffer = await response.arrayBuffer();
  return new File([buffer], filename, { type: mimeType });
};

export const formatDate = (date = "", date_format = "yyyy-MM-dd") => {
  if (!date) {
    return "-";
  } else {
    return format(new Date(date), date_format);
  }
};

export const formatNumber = (value = "", format = "0") => {
  return numeral(value).format(format); // 0.00
};

export const getLookupValueById = (sources: any[], identifier?: string) => {
  return find(sources, (item) => item.id === identifier) || {};
};

export const getBgcolor = (label: ProgressStatus) =>
  label === "completed" ? tokens.brandGreen : label === "remaining" ? tokens.brandBlue : tokens.brandYellow;

export const formatQueryParams = (obj: Record<string, unknown>) => {
  const cleanedObj = omitBy(obj, (v) => isNil(v) || v === "");

  const queryParams = new URLSearchParams(cleanedObj as Record<string, string>).toString();
  return queryParams.length ? `?${queryParams}` : "";
};

export function filterBySearchAndExclude<T extends { id: string }>(
  items: T[] = [],
  excludedIds: string[] = [],
  search: string = "",
  getSearchableText: (item: T) => string,
): T[] {
  if (!search?.length) return items;

  const regex = new RegExp(escapeRegExp(search), "gi");

  return chain(items)
    .filter((item) => !excludedIds.includes(item.id))
    .filter((item) => Boolean(getSearchableText(item).match(regex)))
    .value();
}

export const secondsToHMS = (seconds: number) => {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
}

export const daysFromNow = (dateString: string) => {
  const due_days = moment(dateString).diff(moment(), 'days');
  return due_days;
}

export const getGradebookStatusColor = (status: string) => {
  switch (status) {
    case "locked-in":
      return {
        bgcolor: `${tokens.brandGreen}26`,
        color: tokens.brandGreen,
      };
    case "advisor-approval":
      return {
        bgcolor: `${tokens.brandYellow}26`,
        color: tokens.brandYellow,
      };
    case "registrar-approval":
      return {
        bgcolor: `${tokens.brandBlue}26`,
        color: tokens.brandBlue,
      };
    case "rejected":
      return {
        bgcolor: `${tokens.errorMaterial}26`,
        color: tokens.errorMaterial,
      };
    default:
      return {};
  }
}

export const getGradebookStatusTextColor = (status: string) => {
  switch (status) {
    case "locked-in":
      return tokens.brandGreen
    case "advisor-approval":
      return tokens.brandYellow
    case "registrar-approval":
      return tokens.brandBlue
    case "rejected":
      return tokens.errorMaterial
    default:
      return "text.main";
  }
}

export const getUserStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return {
        bgcolor: `${tokens.brandGreen}26`,
        color: tokens.brandGreen,
      };
    case "inactive":
      return {
        bgcolor: `${tokens.warning}26`,
        color: tokens.warning,
      };
    case "suspended":
      // Red — admin-imposed block. Distinguishes a deliberate access cut
      // from the warmer "expired/old" feel of inactive or archived.
      return {
        bgcolor: `${tokens.statusRejected}26`,
        color: tokens.statusRejected,
      };
    case "archived":
      // Cool gray — historical record, no longer active but preserved.
      return {
        bgcolor: `${tokens.statusNeutral}26`,
        color: tokens.statusNeutral,
      };
    case "draft":
      return {
        bgcolor: `${tokens.statusPurple}26`,
        color: tokens.statusPurple,
      };
    default:
      return {};
  }
}

/**
 * Safely extract a human-readable message from a caught value.
 *
 * `catch` variables are typed `unknown` under `strict`, so reading `.message`
 * off them is unsafe — they may be a string, an Error, an Axios-style
 * `{ message }`, or anything else. This narrows defensively and lets callers
 * drop the unsafe `catch (err: unknown)` annotation entirely.
 */
export const getErrorMessage = (
  err: unknown,
  fallback = "Something went wrong.",
): string => {
  if (typeof err === "string") return err || fallback;
  if (err && typeof err === "object") {
    // Axios-style error: response.data.error takes precedence (server message).
    const responseError = (
      err as { response?: { data?: { error?: unknown } } }
    ).response?.data?.error;
    if (typeof responseError === "string" && responseError) return responseError;
  }
  if (err instanceof Error) return err.message || fallback;
  if (err && typeof err === "object" && "message" in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return fallback;
};

/**
 * Extract the HTTP status code from an Axios-style error (`err.response.status`)
 * without reaching through `any`. Returns `undefined` for non-HTTP errors so
 * callers can do `getErrorStatus(err) === 501` safely on an `unknown` catch.
 */
export const getErrorStatus = (err: unknown): number | undefined => {
  if (err && typeof err === "object") {
    const status = (err as { response?: { status?: unknown } }).response?.status;
    if (typeof status === "number") return status;
  }
  return undefined;
};